import {
  ANSWER_COMMENT_USE_CASE,
  AnswerCommentUseCaseInput,
  AnswerType,
  Attendee,
  AttendeeSubmission,
  CREATE_ANSWER_COMMENT_USE_CASE,
  DecimalNumber,
  DELETE_ANSWER_COMMENT_USE_CASE,
  EssayAnswer,
  ExaminationAssignment,
  FillInBlankGroupAnswer,
  GET_ANSWERS_USE_CASE,
  GET_ASSIGNMENT_BY_ID_USE_CASE,
  GET_ATTENDEE_BY_ID_USE_CASE,
  GET_ATTENDEE_SUBMISSIONS_USE_CASE,
  GetAttendeeSubmissionsQueryParams,
  GET_ATTENDEES_USE_CASE,
  GET_SUBMISSION_BY_ID_USE_CASE,
  GRADING_ANSWER_USE_CASE,
  GRADING_SUBMISSION_USE_CASE,
  GradingAnswerUseCaseInput,
  GradingAnswerUseCaseOutput,
  GradingSubmissionUseCaseOutput,
  MatchingAnswerGroup,
  MultipleChoiceAnswer,
  PUBLISH_SCORE_USE_CASE,
  SingleChoiceAnswer,
  Submission,
  SubmitExaminationSubmissionResponse,
  UPDATE_ANSWER_COMMENT_USE_CASE,
  UPDATE_SUBMISSION_USE_CASE,
  UpdateSubmissionUseCaseOutput,
  SUBMIT_GRADE_USE_CASE,
  REJECT_GRADE_USE_CASE,
  RejectGradeUseCaseInput,
  SubmissionGradingStatus,
  SubmissionResultStatus,
  hasSubmissionSubmitTime,
  isSubmissionGradingTerminal,
} from '@module/assignment';
import { Action, RequestStatus, UseCaseStateGenerator } from '@lib/plugin-redux-core';
import {
  FIND_FORM_RELEASE_BY_ID_USE_CASE,
  FIND_QUESTION_RELEASES_USE_CASE,
  FormRelease,
  QuestionRelease,
  QuestionReleaseProps,
  QuestionReleases,
} from '@module/form';
import {
  AutoGradingUseCaseState,
  GetAssignmentByIdUseCaseState,
  GetAttendeeByIdUseCaseState,
  GetAttendeeSubmissionUseCaseState,
  GetAttendeesUseCaseState,
  GetFormReleaseByIdUseCaseState,
  GetQuestionReleasesUseCaseState,
  GetSubmissionAnswersUseCaseState,
  GetSubmissionByIdUseCaseState,
  GradingSubmissionState,
  UpdateAnswerUseCaseState,
  UpdateAttendeeGradedStateUseCaseState,
  UpdateSubmissionUseCaseState,
} from './grading-submission.states';
const DECIMAL_PLACES = 3;
const calculateTotalScore = (
  answers: (
    | EssayAnswer
    | SingleChoiceAnswer
    | FillInBlankGroupAnswer
    | MultipleChoiceAnswer
    | MatchingAnswerGroup
  )[],
): number =>
  answers.reduce((totalScore, item) => {
    const isFillInBlankAnswer = item.type === AnswerType.FillInBlank;
    if (isFillInBlankAnswer) {
      const fillInBlankAnswer = item as FillInBlankGroupAnswer;

      let fillInBlankAnswerScore = 0;
      fillInBlankAnswer.answers.forEach((answer) => {
        if (answer.hasGraded()) {
          fillInBlankAnswerScore = fillInBlankAnswerScore + answer.score;
        }
      });

      return DecimalNumber.roundNumber(totalScore + fillInBlankAnswerScore, DECIMAL_PLACES);
    }

    const answer = item as SingleChoiceAnswer | MultipleChoiceAnswer | EssayAnswer;
    if (!answer.hasGraded()) {
      return totalScore;
    }
    return DecimalNumber.roundNumber(totalScore + answer.score, DECIMAL_PLACES);
  }, 0);

const calculateMaxTotalScore = (
  questionReleases: QuestionRelease<QuestionReleaseProps>[],
): number =>
  questionReleases.reduce((totalMaxScore, item) => {
    return totalMaxScore + item.score;
  }, 0);

const updateAnswers = (
  answers: (
    | EssayAnswer
    | SingleChoiceAnswer
    | FillInBlankGroupAnswer
    | MultipleChoiceAnswer
    | MatchingAnswerGroup
  )[],
  answer:
    | EssayAnswer
    | SingleChoiceAnswer
    | FillInBlankGroupAnswer
    | MultipleChoiceAnswer
    | MatchingAnswerGroup,

  attendeeList: Attendee[],
  attendee?: Attendee,
): {
  updatedAnswers: (
    | EssayAnswer
    | SingleChoiceAnswer
    | FillInBlankGroupAnswer
    | MultipleChoiceAnswer
    | MatchingAnswerGroup
  )[];
  updatedAttendeeList: Attendee[];
} => {
  const newAnswers = [...answers];
  const updatedAnswerIndex = newAnswers.findIndex((item) => item.questionId === answer.questionId);
  if (updatedAnswerIndex !== -1) {
    const clonedAnswer = Object.assign(Object.create(Object.getPrototypeOf(answer)), answer);
    newAnswers[updatedAnswerIndex] = clonedAnswer;
  }

  const newAttendeeList = [...attendeeList];
  if (attendee) {
    const updatedAttendeeIndex = newAttendeeList.findIndex(
      (item) => item.id.toString() === attendee.id.toString(),
    );
    if (updatedAttendeeIndex !== -1) {
      newAttendeeList[updatedAttendeeIndex] = attendee;
    }
  }

  return {
    updatedAnswers: newAnswers,
    updatedAttendeeList: newAttendeeList,
  };
};

const updateAttendeeList = (attendeeList: Attendee[], attendee: Attendee): Attendee[] => {
  return attendeeList.map((attendeeElement) => {
    if (attendeeElement.id.toString() === attendee.id.toString()) {
      return attendee;
    }
    return attendeeElement;
  });
};

const updateAttendeeListFromSubmission = (
  attendeeList: Attendee[],
  submission: Submission | SubmitExaminationSubmissionResponse,
): Attendee[] =>
  attendeeList.map((attendee) => {
    if (attendee.id.toString() !== submission.attendeeId.toString()) return attendee;

    return Attendee.fromJson({
      ...attendee.toJsonObject(),
      report: {
        ...attendee.report.toJsonObject(),
        ...('expiredAt' in submission ? { expiredAt: submission.expiredAt } : {}),
        ...('gradingStatus' in submission ? { gradingStatus: submission.gradingStatus } : {}),
        ...('rejectionInfo' in submission ? { rejectionInfo: submission.rejectionInfo } : {}),
        ...('status' in submission ? { status: submission.status } : {}),
        submitTime: submission.submitTime,
        ...(submission.result && 'gradedPercentage' in submission.result
          ? {
              score: submission.result.score,
              gradedPercentage: submission.result.gradedPercentage.value,
              isSubmissionPassed:
                submission.result.status?.id?.toString() === SubmissionResultStatus.passed,
            }
          : {}),
      },
    }).getValue();
  });

const mergeSubmitResponseIntoSubmission = (
  submission: Submission | null | undefined,
  response: SubmitExaminationSubmissionResponse,
): Submission | null | undefined => {
  if (!submission || submission.id.toString() !== response.id.toString()) {
    return submission;
  }

  const mergedSubmissionOrError = Submission.create({
    ...submission.toJsonObject(),
    id: response.id,
    attendeeId: response.attendeeId,
    submitTime: response.submitTime,
    result: response.result,
  });

  return mergedSubmissionOrError.isSuccess ? mergedSubmissionOrError.getValue() : submission;
};

export const handleGetAttendeesUseCase: UseCaseStateGenerator<GetAttendeesUseCaseState> = {
  name: GET_ATTENDEES_USE_CASE,
  executing: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      getAttendeeListStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const attendeeList = action.payload as Attendee[];

    return {
      ...state,
      attendeeList: attendeeList,
      getAttendeeListStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const { error } = action;
    return {
      ...state,
      error: error,
      getAttendeeListStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      attendeeList: null,
      getAttendeeListStatus: RequestStatus.RESET,
    };
  },
};

export const handleGetAttendeeByIdUseCase: UseCaseStateGenerator<GetAttendeeByIdUseCaseState> = {
  name: GET_ATTENDEE_BY_ID_USE_CASE,
  executing: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      getAttendeeByIdStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    return {
      ...state,
      attendee: action.payload as Attendee,
      getAttendeeByIdStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const { error } = action;
    return {
      ...state,
      error: error,
      getAttendeeByIdStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      attendee: null,
      getAttendeeByIdStatus: RequestStatus.RESET,
    };
  },
};

export const handleGetFormReleaseByIdUseCase: UseCaseStateGenerator<GetFormReleaseByIdUseCaseState> =
  {
    name: FIND_FORM_RELEASE_BY_ID_USE_CASE,
    executing: (state: GradingSubmissionState): GradingSubmissionState => {
      return {
        ...state,
        getFormReleaseByIdStatus: RequestStatus.EXECUTE,
      };
    },
    success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
      const { payload } = action;

      return {
        ...state,
        formRelease: payload as FormRelease,
        getFormReleaseByIdStatus: RequestStatus.SUCCESS,
      };
    },
    error: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
      const { error } = action;
      return {
        ...state,
        error,
        getFormReleaseByIdStatus: RequestStatus.ERROR,
      };
    },
    reset: (state: GradingSubmissionState): GradingSubmissionState => {
      return {
        ...state,
        getFormReleaseByIdStatus: RequestStatus.RESET,
      };
    },
  };

export const handleGetAssignmentByIdUseCase: UseCaseStateGenerator<GetAssignmentByIdUseCaseState> =
  {
    name: GET_ASSIGNMENT_BY_ID_USE_CASE,
    executing: (state: GradingSubmissionState): GradingSubmissionState => {
      return {
        ...state,
        getAssignmentByIdStatus: RequestStatus.EXECUTE,
      };
    },
    success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
      const { payload } = action;

      const assignment = payload as ExaminationAssignment;

      return {
        ...state,
        assignment,
        getAssignmentByIdStatus: RequestStatus.SUCCESS,
      };
    },
    error: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
      const { error } = action;
      return {
        ...state,
        getAssignmentByIdStatus: RequestStatus.ERROR,
        error,
      };
    },
    reset: (state: GradingSubmissionState): GradingSubmissionState => {
      return {
        ...state,
        getAssignmentByIdStatus: RequestStatus.RESET,
      };
    },
  };

export const handleGetSubmissionById: UseCaseStateGenerator<GetSubmissionByIdUseCaseState> = {
  name: GET_SUBMISSION_BY_ID_USE_CASE,
  executing: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      getSubmissionByIdStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const submission = action.payload as Submission;
    const selectedAttendeeId = state.selectedAttendeeId?.toString();
    const submissionAttendeeId = submission.attendeeId?.toString();
    const attendeeList = updateAttendeeListFromSubmission(state.attendeeList, submission);
    const isSubmitGradeRefresh =
      state.submitGradeSubmissionId?.toString() === submission.id.toString();
    const isSubmitGradeRefreshComplete =
      isSubmitGradeRefresh &&
      (isSubmissionGradingTerminal(submission.gradingStatus) ||
        hasSubmissionSubmitTime(submission.submitTime));
    const completedSubmitGradeState = isSubmitGradeRefreshComplete
      ? {
          submitGradeSubmissionId: null,
          submitGradeAttendeeId: null,
        }
      : {};
    const getSubmissionByIdStatus =
      isSubmitGradeRefresh && !isSubmitGradeRefreshComplete
        ? RequestStatus.ERROR
        : RequestStatus.SUCCESS;
    if (selectedAttendeeId && submissionAttendeeId !== selectedAttendeeId) {
      return {
        ...state,
        attendeeList,
        ...completedSubmitGradeState,
        getSubmissionByIdStatus,
      };
    }

    return {
      ...state,
      submission,
      attendeeList,
      ...completedSubmitGradeState,
      getSubmissionByIdStatus,
    };
  },
  error: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const { error } = action;
    const requestedSubmissionId = action.meta?.input?.toString();
    const isSubmitGradeRefresh =
      requestedSubmissionId && state.submitGradeSubmissionId?.toString() === requestedSubmissionId;
    if (
      requestedSubmissionId &&
      !isSubmitGradeRefresh &&
      (!state.submission || state.submission.id.toString() !== requestedSubmissionId)
    ) {
      return state;
    }

    return {
      ...state,
      getSubmissionByIdStatus: RequestStatus.ERROR,
      error,
    };
  },
  reset: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      getSubmissionByIdStatus: RequestStatus.RESET,
    };
  },
};

export const handleGetQuestionReleasesUseCase: UseCaseStateGenerator<GetQuestionReleasesUseCaseState> =
  {
    name: FIND_QUESTION_RELEASES_USE_CASE,
    executing: (state: GradingSubmissionState): GradingSubmissionState => {
      return {
        ...state,
        getQuestionReleasesStatus: RequestStatus.EXECUTE,
      };
    },
    success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
      const questionReleases = action.payload as QuestionRelease<QuestionReleaseProps>[];
      let sortedQuestions = QuestionReleases.sortQuestionsWithIndexBySectionsOrder(
        questionReleases,
        state.formRelease?.sections,
      );
      // Fallback when section summaries are missing or do not match question section ids
      if ((!sortedQuestions || sortedQuestions.length === 0) && questionReleases?.length > 0) {
        sortedQuestions = QuestionReleases.sortQuestionsWithIndexAndSection(questionReleases);
      }
      const maxScore = calculateMaxTotalScore(questionReleases);
      return {
        ...state,
        questionReleases: sortedQuestions,
        maxScore,
        getQuestionReleasesStatus: RequestStatus.SUCCESS,
      };
    },
    error: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
      const { error } = action;
      return {
        ...state,
        getQuestionReleasesStatus: RequestStatus.ERROR,
        error,
      };
    },
    reset: (state: GradingSubmissionState): GradingSubmissionState => {
      return {
        ...state,
        getQuestionReleasesStatus: RequestStatus.RESET,
      };
    },
  };

export const handleGetAnswersUseCase: UseCaseStateGenerator<GetSubmissionAnswersUseCaseState> = {
  name: GET_ANSWERS_USE_CASE,
  executing: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const requestedSubmissionId = (action.payload as { submissionId?: string })?.submissionId;
    if (
      requestedSubmissionId &&
      (!state.submission || state.submission.id.toString() !== requestedSubmissionId)
    ) {
      return state;
    }

    return {
      ...state,
      getAnswersStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const requestedSubmissionId = (action.meta?.input as { submissionId?: string } | undefined)
      ?.submissionId;
    if (
      requestedSubmissionId &&
      (!state.submission || state.submission.id.toString() !== requestedSubmissionId)
    ) {
      return state;
    }

    const answers = action.payload as (
      | EssayAnswer
      | SingleChoiceAnswer
      | FillInBlankGroupAnswer
      | MultipleChoiceAnswer
      | MatchingAnswerGroup
    )[];

    const totalScore = calculateTotalScore(answers);

    return {
      ...state,
      answers,
      totalScore,
      getAnswersStatus: RequestStatus.SUCCESS,
    };
  },
  error: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const { error } = action;
    const requestedSubmissionId = (action.meta?.input as { submissionId?: string } | undefined)
      ?.submissionId;
    if (
      requestedSubmissionId &&
      (!state.submission || state.submission.id.toString() !== requestedSubmissionId)
    ) {
      return state;
    }

    return {
      ...state,
      error,
      getAnswersStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      getAnswersStatus: RequestStatus.RESET,
      answers: null,
      totalScore: null,
    };
  },
};

export const handleGetAttendeeSubmissionUseCase: UseCaseStateGenerator<GetAttendeeSubmissionUseCaseState> =
  {
    name: GET_ATTENDEE_SUBMISSIONS_USE_CASE,
    executing: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
      const input = action.payload as GetAttendeeSubmissionsQueryParams;
      return {
        ...state,
        selectedAttendeeId: input.attendeeId,
        rejectGradeStatus: RequestStatus.RESET,
        rejectGradeSubmissionId: null,
        rejectedAttendeeBeforeOptimisticUpdate: null,
        getAttendeeSubmissionStatus: RequestStatus.EXECUTE,
      };
    },
    success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
      const attendeeSubmission = action.payload as AttendeeSubmission;
      const requestedAttendeeId = (
        action.meta?.input as GetAttendeeSubmissionsQueryParams | undefined
      )?.attendeeId;
      if (
        state.selectedAttendeeId &&
        requestedAttendeeId &&
        state.selectedAttendeeId.toString() !== requestedAttendeeId.toString()
      ) {
        return state;
      }

      return {
        ...state,
        attendeeSubmission,
        submission: attendeeSubmission?.submissions[0],
        getAttendeeSubmissionStatus: RequestStatus.SUCCESS,
      };
    },
    error: (state: GradingSubmissionState, action: Action) => {
      const { error } = action;
      const requestedAttendeeId = (
        action.meta?.input as GetAttendeeSubmissionsQueryParams | undefined
      )?.attendeeId;
      if (
        requestedAttendeeId &&
        state.selectedAttendeeId &&
        state.selectedAttendeeId.toString() !== requestedAttendeeId.toString()
      ) {
        return state;
      }

      return {
        ...state,
        error: error,
        getAttendeeSubmissionStatus: RequestStatus.ERROR,
      };
    },
    reset: (state: GradingSubmissionState): GradingSubmissionState => {
      return {
        ...state,
        attendeeSubmission: null,
        submission: null,
        getAttendeeSubmissionStatus: RequestStatus.RESET,
      };
    },
  };

export const handleUpdateAttendeeGradedStateUseCase: UseCaseStateGenerator<UpdateAttendeeGradedStateUseCaseState> =
  {
    executing: (state: GradingSubmissionState): GradingSubmissionState => {
      return {
        ...state,
        updateAttendeeGradedStateStatus: RequestStatus.EXECUTE,
      };
    },
    success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
      const updatedAttendee = action.payload as Attendee;

      const foundUpdatedAttendeeIndex = state.attendeeList.findIndex(
        (attendee) => attendee.id.toString() === updatedAttendee.id.toString(),
      );

      state.attendeeList.splice(foundUpdatedAttendeeIndex, 1, updatedAttendee);

      return {
        ...state,
        attendeeList: state.attendeeList,
        updateAttendeeGradedStateStatus: RequestStatus.SUCCESS,
      };
    },
  };

export const handleGradingAnswersUseCase: UseCaseStateGenerator<UpdateAnswerUseCaseState> = {
  name: GRADING_ANSWER_USE_CASE,
  executing: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const useCaseInput = action.payload as GradingAnswerUseCaseInput;
    const { id } = useCaseInput;

    return {
      ...state,
      updateAnswerStatus: RequestStatus.EXECUTE,
      updatedAnswerId: id,
    };
  },
  success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const { answers, attendeeList } = state;
    const { updatedAnswer, updatedAttendee } = action.payload as GradingAnswerUseCaseOutput;

    const { updatedAnswers, updatedAttendeeList } = updateAnswers(
      answers,
      updatedAnswer,
      attendeeList,
      updatedAttendee,
    );
    const totalScore = calculateTotalScore(updatedAnswers);

    return {
      ...state,
      updateAnswerStatus: RequestStatus.SUCCESS,
      answers: updatedAnswers,
      totalScore,
      attendeeList: updatedAttendeeList,
      updatedAnswerId: '',
    };
  },
  error: (state: {}, action: Action) => {
    const { error } = action;
    return {
      ...state,
      updatedAnswerId: '',
      error: error,
      updateAnswerStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      updateAnswerStatus: RequestStatus.RESET,
      updatedAnswerId: '',
    };
  },
};

export const handleAnswerCommentUseCase: UseCaseStateGenerator<UpdateAnswerUseCaseState> = {
  name: ANSWER_COMMENT_USE_CASE,
  executing: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const useCaseInput = action.payload as AnswerCommentUseCaseInput;
    const { id } = useCaseInput;

    return {
      ...state,
      updateCommentStatus: RequestStatus.EXECUTE,
      updatedAnswerId: id,
    };
  },
  success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const { answers, attendeeList } = state;
    const { updatedAnswer } = action.payload;

    const { updatedAnswers } = updateAnswers(answers, updatedAnswer, attendeeList);

    return {
      ...state,
      updateCommentStatus: RequestStatus.SUCCESS,
      answers: updatedAnswers,
      updatedAnswerId: updatedAnswer.id.toString(),
    };
  },
  error: (state: {}, action: Action) => {
    const { error } = action;
    return {
      ...state,
      updatedAnswerId: '',
      error: error,
      updateCommentStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      updateCommentStatus: RequestStatus.RESET,
      updatedAnswerId: '',
    };
  },
};

export const handleUpdateSubmissionUseCase: UseCaseStateGenerator<UpdateSubmissionUseCaseState> = {
  name: UPDATE_SUBMISSION_USE_CASE,
  executing: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      updateSubmissionStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const { submission } = action.payload as UpdateSubmissionUseCaseOutput;
    return {
      ...state,
      updateSubmissionStatus: RequestStatus.SUCCESS,
      submission,
    };
  },
  error: (state: {}, action: Action) => {
    const { error } = action;
    return {
      ...state,
      error: error,
      updateSubmissionStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      updateSubmissionStatus: RequestStatus.RESET,
    };
  },
};

export const handleGradingSubmissionUseCase: UseCaseStateGenerator<AutoGradingUseCaseState> = {
  name: GRADING_SUBMISSION_USE_CASE,
  executing: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      gradingSubmissionStatus: RequestStatus.EXECUTE,
    };
  },
  success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const payload = action.payload as GradingSubmissionUseCaseOutput;

    const { submission, attendee } = payload;
    const updatedAttendeeList = updateAttendeeList(state.attendeeList, attendee);

    return {
      ...state,
      gradingSubmissionStatus: RequestStatus.SUCCESS,
      submission: submission,
      attendeeList: updatedAttendeeList,
    };
  },
  error: (state: {}, action: Action) => {
    const { error } = action;
    return {
      ...state,
      error: error,
      gradingSubmissionStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      gradingSubmissionStatus: RequestStatus.RESET,
    };
  },
};

export const handleSubmitGradeUseCase: UseCaseStateGenerator<GradingSubmissionState> = {
  name: SUBMIT_GRADE_USE_CASE,
  executing: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const submissionId = action.payload as string;
    const isCurrentSubmission = state.submission?.id.toString() === submissionId;
    const attendeeId = isCurrentSubmission ? state.submission.attendeeId.toString() : undefined;

    return {
      ...state,
      submitGradeStatus: RequestStatus.EXECUTE,
      submitGradeSubmissionId: submissionId,
      submitGradeAttendeeId: attendeeId,
    };
  },
  success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const response = action.payload as SubmitExaminationSubmissionResponse;
    const requestedSubmissionId = action.meta?.input?.toString();
    const isCurrentRequest =
      !requestedSubmissionId || state.submitGradeSubmissionId?.toString() === requestedSubmissionId;
    const isCurrentAttendee =
      !state.selectedAttendeeId ||
      response.attendeeId?.toString() === state.selectedAttendeeId.toString();
    const attendeeList = updateAttendeeListFromSubmission(state.attendeeList, response);
    const totalScore =
      typeof response.result?.score === 'number' && Number.isFinite(response.result.score)
        ? response.result.score
        : state.totalScore;

    if (!isCurrentRequest) {
      return {
        ...state,
        attendeeList,
      };
    }

    if (!isCurrentAttendee) {
      return {
        ...state,
        attendeeList,
        submitGradeStatus: RequestStatus.SUCCESS,
        submitGradeSubmissionId: null,
        submitGradeAttendeeId: null,
      };
    }

    return {
      ...state,
      submission: mergeSubmitResponseIntoSubmission(state.submission, response),
      attendeeList,
      totalScore,
      submitGradeStatus: RequestStatus.SUCCESS,
      submitGradeSubmissionId: null,
      submitGradeAttendeeId: null,
    };
  },
  error: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const requestedSubmissionId = action.meta?.input?.toString();

    if (
      requestedSubmissionId &&
      state.submitGradeSubmissionId?.toString() !== requestedSubmissionId
    ) {
      return {
        ...state,
      };
    }

    return {
      ...state,
      error: action.error,
      submitGradeStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: GradingSubmissionState): GradingSubmissionState => ({
    ...state,
    submitGradeStatus: RequestStatus.RESET,
    submitGradeSubmissionId: null,
    submitGradeAttendeeId: null,
  }),
};

export const handleRejectGradeUseCase: UseCaseStateGenerator<GradingSubmissionState> = {
  name: REJECT_GRADE_USE_CASE,
  executing: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const input = action.payload as RejectGradeUseCaseInput;
    const attendeeId = state.submission?.attendeeId?.toString();
    const attendee = state.attendeeList.find((item) => item.id.toString() === attendeeId);
    const optimisticAttendee = attendee
      ? Attendee.fromJson({
          ...attendee.toJsonObject(),
          report: {
            ...attendee.report.toJsonObject(),
            score: 0,
            gradedPercentage: 100,
            isSubmissionPassed: false,
            gradingStatus: {
              id: SubmissionGradingStatus.REJECTED,
              name: SubmissionGradingStatus.REJECTED,
            },
            rejectionInfo: {
              reason: input.reason,
              rejectedAt: Date.now().toString(),
            },
          },
        }).getValue()
      : undefined;

    return {
      ...state,
      attendeeList: optimisticAttendee
        ? updateAttendeeList(state.attendeeList, optimisticAttendee)
        : state.attendeeList,
      rejectedAttendeeBeforeOptimisticUpdate: attendee,
      rejectGradeStatus: RequestStatus.EXECUTE,
      rejectGradeSubmissionId: input.submissionId,
    };
  },
  success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const submission = action.payload as Submission;
    const requestedSubmissionId = (action.meta?.input as RejectGradeUseCaseInput | undefined)
      ?.submissionId;
    const isCurrentRequest =
      !requestedSubmissionId ||
      state.rejectGradeSubmissionId?.toString() === requestedSubmissionId.toString();
    const isCurrentAttendee =
      !state.selectedAttendeeId ||
      submission.attendeeId?.toString() === state.selectedAttendeeId.toString();
    const attendeeList = updateAttendeeListFromSubmission(state.attendeeList, submission);

    if (!isCurrentRequest || !isCurrentAttendee) {
      return {
        ...state,
        attendeeList,
      };
    }

    return {
      ...state,
      submission,
      attendeeList,
      rejectGradeStatus: RequestStatus.SUCCESS,
      rejectGradeSubmissionId: null,
      rejectedAttendeeBeforeOptimisticUpdate: null,
    };
  },
  error: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const requestedSubmissionId = (action.meta?.input as RejectGradeUseCaseInput | undefined)
      ?.submissionId;
    if (
      requestedSubmissionId &&
      state.rejectGradeSubmissionId?.toString() !== requestedSubmissionId.toString()
    ) {
      return state;
    }

    const attendee = state.rejectedAttendeeBeforeOptimisticUpdate;

    return {
      ...state,
      attendeeList: attendee
        ? updateAttendeeList(state.attendeeList, attendee)
        : state.attendeeList,
      error: action.error,
      rejectGradeStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: GradingSubmissionState): GradingSubmissionState => ({
    ...state,
    rejectGradeStatus: RequestStatus.RESET,
    rejectGradeSubmissionId: null,
    rejectedAttendeeBeforeOptimisticUpdate: null,
  }),
};

export const handleCreateAnswerCommentUseCase: UseCaseStateGenerator<UpdateAnswerUseCaseState> = {
  name: CREATE_ANSWER_COMMENT_USE_CASE,
  executing: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const useCaseInput = action.payload as AnswerCommentUseCaseInput;
    const { id } = useCaseInput;

    return {
      ...state,
      updateCommentStatus: RequestStatus.EXECUTE,
      updatedAnswerId: id,
    };
  },
  success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const { answers, attendeeList } = state;
    const updatedAnswer = action.payload;

    const { updatedAnswers } = updateAnswers(answers, updatedAnswer, attendeeList);

    return {
      ...state,
      updateCommentStatus: RequestStatus.SUCCESS,
      answers: updatedAnswers,
      updatedAnswerId: '',
    };
  },
  error: (state: {}, action: Action) => {
    const { error } = action;
    return {
      ...state,
      updatedAnswerId: '',
      error: error,
      updateCommentStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      updateCommentStatus: RequestStatus.RESET,
      updatedAnswerId: '',
    };
  },
};

export const handleUpdateAnswerCommentUseCase: UseCaseStateGenerator<UpdateAnswerUseCaseState> = {
  name: UPDATE_ANSWER_COMMENT_USE_CASE,
  executing: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const useCaseInput = action.payload as AnswerCommentUseCaseInput;
    const { id } = useCaseInput;

    return {
      ...state,
      updateCommentStatus: RequestStatus.EXECUTE,
      updatedAnswerId: id,
    };
  },
  success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const { answers, attendeeList } = state;
    const updatedAnswer = action.payload;
    const { updatedAnswers } = updateAnswers(answers, updatedAnswer, attendeeList);

    return {
      ...state,
      updateCommentStatus: RequestStatus.SUCCESS,
      answers: updatedAnswers,
      updatedAnswerId: updatedAnswer.id.toString(),
    };
  },
  error: (state: {}, action: Action) => {
    const { error } = action;
    return {
      ...state,
      updatedAnswerId: '',
      error: error,
      updateCommentStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      updateCommentStatus: RequestStatus.RESET,
      updatedAnswerId: '',
    };
  },
};

export const handleDeleteAnswerCommentUseCase: UseCaseStateGenerator<UpdateAnswerUseCaseState> = {
  name: DELETE_ANSWER_COMMENT_USE_CASE,
  executing: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const useCaseInput = action.payload as AnswerCommentUseCaseInput;
    const { id } = useCaseInput;

    return {
      ...state,
      updateCommentStatus: RequestStatus.EXECUTE,
      updatedAnswerId: id,
    };
  },
  success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const { answers, attendeeList } = state;
    const updatedAnswer = action.payload;

    const { updatedAnswers } = updateAnswers(answers, updatedAnswer, attendeeList);

    return {
      ...state,
      updateCommentStatus: RequestStatus.SUCCESS,
      answers: updatedAnswers,
      updatedAnswerId: '',
    };
  },
  error: (state: {}, action: Action) => {
    const { error } = action;
    return {
      ...state,
      updatedAnswerId: '',
      error: error,
      updateCommentStatus: RequestStatus.ERROR,
    };
  },
  reset: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
      updateCommentStatus: RequestStatus.RESET,
      updatedAnswerId: '',
    };
  },
};

export const handlePublishScoreUseCaseForGradingSubmission = {
  name: PUBLISH_SCORE_USE_CASE,
  executing: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
    };
  },
  success: (state: GradingSubmissionState, action: Action): GradingSubmissionState => {
    const updatedAssignment = action.payload as ExaminationAssignment;
    return {
      ...state,
      assignment: updatedAssignment,
    };
  },
  error: (state: GradingSubmissionState): GradingSubmissionState => {
    return {
      ...state,
    };
  },
};

export const gradingSubmissionHandlers = [
  handleGetAttendeesUseCase,
  handleGetAttendeeByIdUseCase,
  handleGetFormReleaseByIdUseCase,
  handleGetAssignmentByIdUseCase,
  handleGetSubmissionById,
  handleGetQuestionReleasesUseCase,
  handleGetAnswersUseCase,
  handleGetAttendeeSubmissionUseCase,
  handleUpdateAttendeeGradedStateUseCase,
  handleGradingAnswersUseCase,
  handleAnswerCommentUseCase,
  handleUpdateSubmissionUseCase,
  handleGradingSubmissionUseCase,
  handleSubmitGradeUseCase,
  handleRejectGradeUseCase,
  handleCreateAnswerCommentUseCase,
  handleUpdateAnswerCommentUseCase,
  handleDeleteAnswerCommentUseCase,
  handlePublishScoreUseCaseForGradingSubmission,
];
